import React from 'react';
import { MapPin, Calendar, Globe, ArrowUpRight } from 'lucide-react';
import SectionHeader from './SectionHeader';
import { useLanguage } from '../language/LanguageContext';
import MetaItem from './ui/MetaItem';
import Reveal from './ui/Reveal';

interface Experience {
  title: string;
  role: string;
  period: string;
  location: string;
  description: string;
  highlights?: string[];
  url?: string;
}

/** A period of "Present" (or its pt-BR equivalent) marks the current role. */
const isCurrent = (period: string) => /present|atual/i.test(period);

const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

const ExperienceSection = () => {
  const { translations } = useLanguage();
  const { experience } = translations;

  const toExperience = (key: string): Experience => {
    const item = (experience as Record<string, any>)[key];
    return {
      title: item.company,
      role: item.role,
      period: item.period,
      location: item.location,
      description: item.description,
      highlights: item.highlights,
      url: item.url
    };
  };

  const groups: { title: string; items: Experience[] }[] = [
    {
      title: experience.professionalTitle,
      items: ['rise', 'voyager', 'medprev', 'cargon'].map(toExperience)
    },
    {
      title: experience.founderTitle,
      items: ['remi'].map(toExperience)
    },
    {
      title: experience.researchTitle,
      items: ['automation', 'quantumTeam', 'plusEnergy'].map(toExperience)
    }
  ];

  return (
    <div className="space-y-16">
      {groups.map((group) => (
        <section key={group.title}>
          <SectionHeader title={group.title} />

          <div className="relative pl-8 ml-2 max-w-3xl">
            {/* Gradient rail that fades out at the end of the list */}
            <div
              aria-hidden="true"
              className="absolute left-0 top-2 bottom-0 w-px bg-rail"
            />

            {group.items.map((exp, index) => (
              <Reveal key={`${group.title}-${index}`} delay={index * 70}>
                <ExperienceCard
                  experience={exp}
                  isLast={index === group.items.length - 1}
                  visitLabel={experience.visitSite}
                />
              </Reveal>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

const ExperienceCard = ({
  experience,
  isLast,
  visitLabel
}: {
  experience: Experience;
  isLast: boolean;
  visitLabel: string;
}) => {
  const { title, role, period, location, description, highlights, url } = experience;
  const current = isCurrent(period);

  return (
    <div className={`relative ${isLast ? '' : 'mb-8'}`}>
      {/* Timeline node */}
      <span
        aria-hidden="true"
        className={`absolute -left-8 top-6 flex items-center justify-center w-3 h-3 -translate-x-1/2 rounded-full ${
          current ? 'bg-accent' : 'bg-dark-border-strong'
        }`}
      >
        {current && (
          <span className="absolute inset-0 rounded-full bg-accent animate-pulse-ring" />
        )}
      </span>

      <div className="card-interactive p-5">
        <div className="flex items-start gap-3 mb-3">
          <span
            aria-hidden="true"
            className={`flex items-center justify-center w-10 h-10 rounded-xl text-xs font-bold flex-shrink-0 border ${
              current
                ? 'bg-accent/10 border-accent/25 text-accent'
                : 'bg-dark-bg border-dark-border text-dark-muted'
            }`}
          >
            {initials(title)}
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1 md:gap-3">
              <h3 className="text-lg font-semibold text-dark-heading truncate">{title}</h3>
              <div className="flex items-center gap-2 flex-shrink-0">
                {current && (
                  <span className="px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-accent bg-accent/10 border border-accent/30 rounded-full">
                    {period}
                  </span>
                )}
                {!current && <MetaItem icon={Calendar}>{period}</MetaItem>}
              </div>
            </div>
            <p className="text-dark-text font-medium">{role}</p>
          </div>
        </div>

        <div className="mb-4">
          <MetaItem icon={MapPin}>{location}</MetaItem>
        </div>

        <p className="text-dark-text leading-relaxed mb-4">{description}</p>

        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-1.5 mb-4 text-sm font-medium text-accent hover:text-accent-soft transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-dark-surface rounded"
          >
            <Globe size={14} />
            {visitLabel}
            <ArrowUpRight
              size={14}
              className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </a>
        )}

        {highlights && highlights.length > 0 && (
          <ul className="space-y-2 pt-4 border-t border-dark-border">
            {highlights.map((highlight, index) => (
              <li key={index} className="flex items-start gap-3 text-sm text-dark-text">
                <span
                  aria-hidden="true"
                  className="w-1.5 h-1.5 rounded-full bg-accent/70 mt-[0.5rem] flex-shrink-0"
                />
                <span className="leading-relaxed">{highlight}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ExperienceSection;
